// Protected RBAC Route Wrapper
// Date: January 13, 2025
// Purpose: Automatically protects routes with RBAC based on current path
// This component wraps routes and automatically applies RBAC protection

import React from 'react';
import { useLocation } from 'react-router-dom';
import { RBACRoute } from './RBACRoute';
import { getScreenCodeForRoute, getBasePathForRBAC, shouldProtectRoute } from '@/config/routeProtection';

interface ProtectedRBACRouteProps {
  children: React.ReactNode;
  /**
   * Optional: Override route path (defaults to current location)
   */
  routePath?: string;
  /**
   * Optional: Override screen code (defaults to lookup from route)
   */
  screenCode?: string;
}

/**
 * ProtectedRBACRoute Component
 * 
 * Automatically protects routes with RBAC based on the current route path.
 * This is a convenience wrapper around RBACRoute that auto-detects the route
 * and screen code from the current location.
 * 
 * Usage:
 * ```tsx
 * <Route path="/dashboard" element={
 *   <ProtectedRBACRoute>
 *     <DashboardPage />
 *   </ProtectedRBACRoute>
 * } />
 * ```
 */
export const ProtectedRBACRoute: React.FC<ProtectedRBACRouteProps> = ({
  children,
  routePath,
  screenCode
}) => {
  const location = useLocation();
  const rawPath = routePath || location.pathname;
  // Use base path for RBAC so sub-routes (e.g. /stakeholders/create) use parent screen permission
  const currentPath = getBasePathForRBAC(rawPath);

  // If route shouldn't be protected, just render children
  if (!shouldProtectRoute(rawPath)) {
    return <>{children}</>;
  }

  // Auto-detect screen code if not provided (use raw path so we get correct code for sub-routes)
  const detectedScreenCode = screenCode || getScreenCodeForRoute(rawPath) || undefined;

  return (
    <RBACRoute
      routePath={currentPath}
      screenCode={detectedScreenCode}
      autoDetect={!routePath && !screenCode}
    >
      {children}
    </RBACRoute>
  );
};

